import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { fmtDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowRightLeft, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { in: "Masuk", out: "Keluar", return: "Retur", adjustment: "Penyesuaian" };
const TYPE_COLOR = { in: "bg-emerald-100 text-emerald-700", out: "bg-rose-100 text-rose-700", return: "bg-amber-100 text-amber-700", adjustment: "bg-blue-100 text-blue-700" };

export default function Stock() {
  const { isViewOnly } = useAuth();
  const { refreshLowStock } = useApp();
  const [muts, setMuts] = useState([]);
  const [items, setItems] = useState([]);
  const [mutForm, setMutForm] = useState(null);
  const [opForm, setOpForm] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [timeSort, setTimeSort] = useState("desc");
  const [dateFilter, setDateFilter] = useState("");

  const load = () => { api.get("/stock-mutations").then((r) => setMuts(r.data)); api.get("/items").then((r) => setItems(r.data)); };
  useEffect(() => { load(); }, []);

  const localDate = (ts) => { try { return new Date(ts).toLocaleDateString("en-CA"); } catch { return ""; } };
  const filtered = muts
    .filter((m) => (!q || (m.item_name || "").toLowerCase().includes(q.toLowerCase()))
      && (typeFilter === "all" || m.type === typeFilter)
      && (!dateFilter || localDate(m.timestamp) === dateFilter))
    .sort((a, b) => timeSort === "desc" ? (b.timestamp || "").localeCompare(a.timestamp || "") : (a.timestamp || "").localeCompare(b.timestamp || ""));

  const saveMut = async () => {
    try { await api.post("/stock-mutations", mutForm); toast.success("Mutasi tercatat"); setMutForm(null); load(); refreshLowStock(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveOp = async () => {
    try { const { data } = await api.post("/stock-opname", opForm); toast.success(`Opname selesai. Selisih ${data.diff}`); setOpForm(null); load(); refreshLowStock(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="stock-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Stok & Opname</h1>
          <p className="text-sm text-slate-500">Riwayat mutasi stok & penyesuaian fisik</p>
        </div>
        {!isViewOnly && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMutForm({ item_id: "", type: "adjustment", qty: 0, note: "" })} data-testid="add-mutation-button">
              <ArrowRightLeft className="h-4 w-4 mr-1.5" /> Mutasi Manual
            </Button>
            <Button onClick={() => setOpForm({ item_id: "", physical_stock: 0, note: "" })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-opname-button">
              <ClipboardCheck className="h-4 w-4 mr-1.5" /> Stok Opname
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari barang..." className="max-w-xs" data-testid="stock-search-input" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44" data-testid="stock-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Jenis</SelectItem>
            <SelectItem value="in">Masuk</SelectItem>
            <SelectItem value="out">Keluar</SelectItem>
            <SelectItem value="return">Retur</SelectItem>
            <SelectItem value="adjustment">Penyesuaian</SelectItem>
          </SelectContent>
        </Select>
        <Select value={timeSort} onValueChange={setTimeSort}>
          <SelectTrigger className="w-44" data-testid="stock-time-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Waktu: Terbaru</SelectItem>
            <SelectItem value="asc">Waktu: Terlama</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-44" data-testid="stock-date-filter" />
        {dateFilter && <Button variant="outline" onClick={() => setDateFilter("")} data-testid="stock-date-clear">Reset Tanggal</Button>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Waktu</TableHead><TableHead>Barang</TableHead><TableHead>Jenis</TableHead>
            <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Stok Akhir</TableHead>
            <TableHead>Keterangan</TableHead><TableHead>User</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="text-xs text-slate-500">{fmtDateTime(m.timestamp)}</TableCell>
                <TableCell className="font-medium">{m.item_name}</TableCell>
                <TableCell><Badge className={`${TYPE_COLOR[m.type]} hover:${TYPE_COLOR[m.type]}`}>{TYPE_LABEL[m.type]}</Badge></TableCell>
                <TableCell className="text-right mono">{m.qty}</TableCell>
                <TableCell className="text-right mono">{m.stock_after}</TableCell>
                <TableCell className="text-xs text-slate-500">{m.note}</TableCell>
                <TableCell className="text-xs">{m.username}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Belum ada mutasi</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Mutation dialog */}
      <Dialog open={!!mutForm} onOpenChange={(o) => !o && setMutForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Mutasi Stok Manual</DialogTitle></DialogHeader>
          {mutForm && (
            <div className="space-y-3">
              <ItemSelect items={items} value={mutForm.item_id} onChange={(v) => setMutForm({ ...mutForm, item_id: v })} />
              <div className="space-y-1"><Label>Jenis</Label>
                <Select value={mutForm.type} onValueChange={(v) => setMutForm({ ...mutForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Masuk</SelectItem><SelectItem value="out">Keluar</SelectItem>
                    <SelectItem value="return">Retur</SelectItem><SelectItem value="adjustment">Penyesuaian (+/-)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Qty {mutForm.type === "adjustment" && "(boleh negatif)"}</Label>
                <Input type="number" value={mutForm.qty} onChange={(e) => setMutForm({ ...mutForm, qty: Number(e.target.value) })} data-testid="mutation-qty-input" /></div>
              <div className="space-y-1"><Label>Keterangan</Label><Input value={mutForm.note} onChange={(e) => setMutForm({ ...mutForm, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveMut} className="bg-orange-500 hover:bg-orange-600" data-testid="save-mutation-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opname dialog */}
      <Dialog open={!!opForm} onOpenChange={(o) => !o && setOpForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Form Stok Opname</DialogTitle></DialogHeader>
          {opForm && (
            <div className="space-y-3">
              <ItemSelect items={items} value={opForm.item_id} onChange={(v) => setOpForm({ ...opForm, item_id: v })} />
              {opForm.item_id && <div className="text-xs text-slate-500">Stok sistem: <b>{items.find((i) => i.id === opForm.item_id)?.stock}</b></div>}
              <div className="space-y-1"><Label>Stok Fisik Aktual</Label>
                <Input type="number" value={opForm.physical_stock} onChange={(e) => setOpForm({ ...opForm, physical_stock: Number(e.target.value) })} data-testid="opname-physical-input" /></div>
              <div className="space-y-1"><Label>Catatan</Label><Input value={opForm.note} onChange={(e) => setOpForm({ ...opForm, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveOp} className="bg-orange-500 hover:bg-orange-600" data-testid="save-opname-button">Simpan Opname</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItemSelect({ items, value, onChange }) {
  return (
    <div className="space-y-1"><Label>Barang</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid="mutation-item-select"><SelectValue placeholder="Pilih barang" /></SelectTrigger>
        <SelectContent className="max-h-64">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.barcode})</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
